'use client';

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, TextField, MenuItem, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { trackEvent, trackException } from '../../utils/appInsights';
import { useAuth } from '../../context/AuthContext';

const SupportRequests = () => {
  const { isUnknownUser, permsLoading } = useAuth();

  // fetched Azure user info
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  // for access‐request mode
  const [accessName, setAccessName] = useState('');
  const [accessEmail, setAccessEmail] = useState('');
  const [managerName, setManagerName] = useState('');
  // for support‐request mode
  const [supportGroup, setSupportGroup] = useState('');
  const [severity, setSeverity] = useState('');
  const [title, setTitle] = useState('');

  //Shared
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const isAccessMode = isUnknownUser;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get('/.auth/me', { withCredentials: true });
        const user = res.data?.clientPrincipal;
        if (user) {
          const email = user.userDetails;
          const name = email.split('@')[0];
          setUserInfo({ name, email });
          setAccessName(name);
          setAccessEmail(email);
        } else {
          setUserInfo({ name: '', email: '' });
        }
      } catch (err) {
        console.error('[Auth] Error fetching user info:', err);
        setUserInfo({ name: '', email: '' });
      }
    };
    fetchUser();
  }, []);

  const resetForm = () => {
    setSupportGroup('');
    setSeverity('');
    setTitle('');
    setDescription('');
    setFile(null);
    if (isAccessMode) {
      setAccessName('');
      setAccessEmail('');
      setManagerName('');
    }
  };

  const postToMonday = async (payload) => {
    try {
      const response = await axios.post('/api/Monday/json', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      trackException(new Error('Error posting to Monday API'), 3, { payload });
      return null;
    }
  };

  const createMondayTask = async ({ itemName, supportGroup, severity }) => {
    const today = new Date().toISOString().slice(0, 10);
    const query = `mutation ($myItemName: String!, $myGroupId: String!, $columnVals: JSON!) {
      create_item (board_id:6188283523, group_id:$myGroupId, item_name:$myItemName, column_values:$columnVals) { id }
    }`;

    const groupId = isAccessMode
      ? 'new_group__1'
      : supportGroup || 'topics';

    const variables = {
      myItemName: itemName,
      myGroupId: groupId,
      columnVals: JSON.stringify({
        status: { label: 'Not Started' },
        date4: { date: today },
        priority: { label: isAccessMode ? 'High' : (severity || 'Low') },
      }),
    };

    const responseBody = await postToMonday({ query, variables });
    return responseBody?.data?.create_item?.id || null;
  };

  const appendTaskUpdateText = async ({ taskId, description }) => {
    const query = `mutation ($myItemId: ID, $myTextBody: String!) {
      create_update (item_id:$myItemId, body:$myTextBody) { id }
    }`;

    const variables = {
      myItemId: taskId,
      myTextBody: description,
    };

    const responseBody = await postToMonday({ query, variables });
    return responseBody?.data?.create_update?.id || null;
  };

  const sendMondayNotification = async ({ notificationText, userId }) => {
    const query = `mutation ($myNotificationText: String!, $recipient: ID!) {
      create_notification (text:$myNotificationText, user_id:$recipient, target_id:6188283523, target_type: Project) { id }
    }`;

    const variables = { myNotificationText: notificationText, recipient: userId };
    const responseBody = await postToMonday({ query, variables });
    return responseBody?.data?.create_notification?.id || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Build two different payload bodies:
      let fullDescription;
      if (isAccessMode) {
        fullDescription = 
          `Access Request Submitted By: ${accessName} / ${accessEmail}\n` +
          `Manager: ${managerName}\n\n` +
          `Reason For Access:\n${description}`;
      } else {
        fullDescription =
          `Submitted By: ${userInfo.name} / ${userInfo.email}\n\n` +
          `Description:\n${description}`;
      }
      // Step 1: Create Monday Item
      const itemName = isAccessMode
        ? `Access Request: Direct report of ${managerName}`
        : title;
      const priority = isAccessMode ? 'High' : severity;
      const taskId = await createMondayTask({ itemName, supportGroup, severity: priority });
      if (!taskId) throw new Error('Task creation failed');

      // Step 2: Add description as update
      const updateId = await appendTaskUpdateText({
        taskId,
        description: fullDescription
      });
      if (!updateId) throw new Error('Failed to append update text');

      // Step 3: Upload file to the update (if a file was selected)
      if (file) {
        const formData = new FormData();
        const fileUploadQuery = `
          mutation ($file: File!) {
            add_file_to_update(update_id: ${updateId}, file: $file) {
              id
            }
          }
        `;
        formData.append('query', fileUploadQuery);
        formData.append('file', file);

        const uploadResponse = await axios.post('/api/Monday/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (!uploadResponse?.data?.data?.add_file_to_update?.id) {
          throw new Error('File upload failed');
        }
      }

      // Step 4: Notify selected users
      const alertIds = ['55328009', '42342208', '69200995'];
      await Promise.all(
        alertIds.map((id) =>
          sendMondayNotification({
            notificationText: isAccessMode
              ? 'New Access Request Submitted'
              : 'New Support Request Submitted',
            userId: id,
          })
        )
      );

      // Step 5: Track and reset
      trackEvent(
        isAccessMode ? 'AccessRequestSubmitted' : 'SupportRequestSubmitted',
        {
          supportGroup,
          priority,
          itemName,
          user: isAccessMode ? accessEmail : userInfo.email,
        }
      );

      setSuccessMessage('Submission Successful!');
      resetForm();
      setFile(null); // Clear file input
    } catch (error) {
      console.error('Submission error:', error);
      trackException(error, 3, { supportGroup, severity, title });
      setErrorMessage('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (permsLoading) {
    return (
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
          <Typography variant="h6">Loading User Permissions</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9f9f9',
        p: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          border: '1px solid #ddd',
          borderRadius: '8px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', color: '#1976d2' }}>
          {isAccessMode ? 'Request Access' : 'Support Request'}
        </Typography>

        <form onSubmit={handleSubmit}>
          {isAccessMode ? (
            <>
              <TextField
                label="Your Name"
                value={accessName}
                onChange={e => setAccessName(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="Your Email"
                type="email"
                value={accessEmail}
                onChange={e => setAccessEmail(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="Your Manager's Name"
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="Reason For Access"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                required
                multiline
                rows={4}
                margin="normal"
              />
            </>
          ) : (
            <>
              <TextField
                select
                label="Support Type"
                value={supportGroup}
                onChange={(e) => setSupportGroup(e.target.value)}
                fullWidth
                required
                margin="normal"
              >
                <MenuItem value="group_title">Report a Bug</MenuItem>
                <MenuItem value="topics">Request a Feature</MenuItem>
              </TextField>
              <TextField
                select
                label="Severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                fullWidth
                required
                margin="normal"
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </TextField>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                required
                multiline
                rows={4}
                margin="normal"
              />
              <Button variant="outlined" component="label" fullWidth sx={{ mt: 2 }}>
                {file ? file.name : 'Attach a File (optional)'}
                <input type="file" hidden onChange={(e) => setFile(e.target.files[0])} />
              </Button>
            </>
          )}

          <Box mt={2} display="flex" justifyContent="center">
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading
                ? <CircularProgress size={24}/>
                : isAccessMode
                  ? 'Request Access'
                  : 'Submit Request'}
            </Button>
          </Box>
        </form>

        {successMessage && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default SupportRequests;
